import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form } from '@components/atoms/Form';
import { RoundProfilePicture } from '@components/atoms/RoundProfilePicture';
import { Space } from '@components/atoms/Space';
import { Button, ButtonMode } from '@components/atoms/Button';
import { useDeleteVehicleRequest } from '@services/useDeleteVehicleRequest';
import { useNotifications } from '@hooks/useNotifications';
import styles from './DeleteVehicleForm.module.css';
import { RoundIcon, RoundIconVariants } from '@components/atoms/RoundIcon';

type DeleteVehicleFormProps = {
    vehicleUuid: string;
    onDeleted?: () => void;
};

export const DeleteVehicleForm: FC<DeleteVehicleFormProps> = ({ vehicleUuid, onDeleted }) => {
    const { t } = useTranslation();
    const { showDefaultNotification } = useNotifications();

    const { request, loading, lastResponseTime, error, success } = useDeleteVehicleRequest();

    const formRef = useRef<HTMLFormElement>(null);
    const [showConfirm, setShowConfirm] = useState(false);

    const translations = {
        yes: t('common.yes'),
        no: t('common.no'),
        title: t('user-area.delete-vehicle'),
        warning: t('user-area.delete-vehicle-warning'),
    };

    const handleOpenConfirm = () => {
        if (loading) return;
        setShowConfirm(true);
    };

    const handleConfirmYes = () => {
        formRef.current?.requestSubmit();
        setShowConfirm(false);
    };

    const handleConfirmNo = () => {
        setShowConfirm(false);
    };

    const submitForm = () => {
        request(vehicleUuid);
    };

    useEffect(() => {
        if (!lastResponseTime) return;

        if (error) {
            showDefaultNotification({ type: 'error', message: error });
            return;
        }

        if (success) {
            showDefaultNotification({ type: 'success', message: success });
            onDeleted?.();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastResponseTime]);

    return (
        <Form formRef={formRef} onSubmit={submitForm}>
            <span
                onClick={handleOpenConfirm}
                style={{
                    cursor: loading ? 'default' : 'pointer',
                    opacity: loading ? 0.5 : 1,
                }}>
                <RoundIcon icon="fa-light fa-trash-can" variant={RoundIconVariants.variant2} />
            </span>

            <>
                {showConfirm && (
                    <div className={styles.overlay}>
                        <div className={styles.pickerContainer}>
                            <RoundProfilePicture
                                icon="fa-thin fa-triangle-exclamation"
                                labelText={translations.title}
                                hintText={translations.warning}
                            />

                            <Space spaceBreakpoints={{ xs: { space: 30 } }} />

                            <Button
                                label={translations.yes}
                                mode={ButtonMode.primary}
                                classes={styles.button}
                                onClick={handleConfirmYes}
                            />

                            <Space spaceBreakpoints={{ xs: { space: 15 } }} />

                            <Button
                                label={translations.no}
                                mode={ButtonMode.primary}
                                outline
                                classes={styles.button}
                                onClick={handleConfirmNo}
                            />
                        </div>
                    </div>
                )}
            </>
        </Form>
    );
};
